import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES_LIST = [
  "Impresión de Documentos",
  "Impresión de Planos",
  "Fotocopias",
  "Escaneo Digital",
  "Asesoría Visa Americana (DS-160)",
  "Accesorios Electrónicos",
  "Otro",
];

const CONTACT_INFO = [
  {
    icon: Phone,
    label: "Teléfono / WhatsApp",
    value: "8998 4547",
    href: "tel:+50589984547",
  },
  {
    icon: Mail,
    label: "Correo Electrónico",
    value: "printacce@gmail.com",
    href: "mailto:printacce@gmail.com",
  },
  {
    icon: MapPin,
    label: "Dirección",
    value: "Entrada UCC, 1/2 cuadra arriba y 1/2 abajo, casa 93. Altamira, Managua",
    href: "https://maps.google.com/?q=Universidad+Centroamericana+Managua+Nicaragua",
  },
  {
    icon: Clock,
    label: "Horario",
    value: "Lun–Vie: 8:00 AM – 5:00 PM | Sáb: 8:00 AM – 4:00 PM",
    href: null,
  },
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contacto" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E65B78] mb-3">Contacto</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#111111] mb-4">
            Estamos Aquí Para Ayudarte
          </h2>
          <p className="text-base md:text-lg text-[#5C5C5C] max-w-xl mx-auto">
            Visítanos en persona, llámanos o escríbenos. Respondemos rápido.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Left: Form */}
          <div className="bg-[#FAFAFA] rounded-3xl p-8 border border-gray-100">
            <h3 className="text-xl font-bold text-[#111111] mb-6">Envíanos un Mensaje</h3>

            {status === "success" ? (
              <div
                data-testid="contact-success-msg"
                className="flex flex-col items-center justify-center gap-4 py-12 text-center"
              >
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle size={40} className="text-green-600" />
                </div>
                <h4 className="text-xl font-bold text-[#111111]">Mensaje Enviado</h4>
                <p className="text-[#5C5C5C] text-sm max-w-xs">
                  Recibimos tu mensaje. Te contactaremos pronto por teléfono o correo.
                </p>
                <button
                  onClick={() => setStatus(null)}
                  className="text-[#E65B78] font-bold text-sm underline"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#111111] mb-1.5">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Tu nombre"
                      data-testid="contact-name-input"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#E65B78] focus:ring-2 focus:ring-[#E65B78]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111111] mb-1.5">
                      Teléfono / WhatsApp
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="8888 0000"
                      data-testid="contact-phone-input"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#E65B78] focus:ring-2 focus:ring-[#E65B78]/20 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111111] mb-1.5">
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="tucorreo@email.com"
                    data-testid="contact-email-input"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#E65B78] focus:ring-2 focus:ring-[#E65B78]/20 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111111] mb-1.5">
                    Servicio de Interés
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    data-testid="contact-service-select"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#111111] focus:outline-none focus:border-[#E65B78] focus:ring-2 focus:ring-[#E65B78]/20 transition-all"
                  >
                    <option value="">Seleccionar servicio...</option>
                    {SERVICES_LIST.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111111] mb-1.5">
                    Mensaje *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Cuéntanos en qué podemos ayudarte..."
                    data-testid="contact-message-textarea"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#E65B78] focus:ring-2 focus:ring-[#E65B78]/20 transition-all resize-none"
                  />
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-sm" data-testid="contact-error-msg">
                    Hubo un error al enviar. Por favor intenta de nuevo.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#E65B78] hover:bg-[#D44B66] disabled:opacity-60 text-white font-bold px-7 py-3.5 rounded-full transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  {status === "loading" ? "Enviando..." : (
                    <>
                      Enviar Mensaje
                      <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right: Map + Info */}
          <div className="flex flex-col gap-6">
            {/* Google Map */}
            <div className="rounded-3xl overflow-hidden shadow-md border border-gray-100 flex-1 min-h-[280px]">
              <iframe
                title="Ubicación Printacce"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3871.8763221!2d-86.2818!3d12.1018!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8f7156e21e02e6db%3A0xf7e5698ca90ff0b5!2sUniversidad%20Centroamericana%20(UCC)!5e0!3m2!1ses!2sni!4v1711234567890!5m2!1ses!2sni"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CONTACT_INFO.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="bg-[#FAFAFA] rounded-2xl p-4 border border-gray-100 hover:border-[#E65B78]/20 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="bg-[#FFE4E8] rounded-xl p-2 flex-shrink-0">
                      <Icon size={16} className="text-[#E65B78]" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#5C5C5C] uppercase tracking-wide mb-1">{label}</p>
                      {href ? (
                        <a
                          href={href}
                          target={href.startsWith("http") ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-[#111111] hover:text-[#E65B78] transition-colors leading-snug"
                        >
                          {value}
                        </a>
                      ) : (
                        <p className="text-sm font-semibold text-[#111111] leading-snug">{value}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
